// HeroForge (docs/17 §4): the character-creation panel of the pre-game World lobby. Choices on the
// left (name, class, species, build, background, loadout), a sticky preview on the right that is
// exactly `derive_stats` over the kit `forge_hero` will grant (`WasmSimBridge.previewHero`).
// docs/59: a class choice ("calling"), details sheets on every choice card, and the hero is forged
// with `forge_hero_called`. docs/80: each choice is a slot naming the current pick; its tiles open
// only when the slot is clicked, one at a time, and close again on a pick.
// Built only from the component library; layout lives in forge.css under `fk-forge__*`.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge, Banner, Button, Card, ClassIcon, DyeSwatches, Field, HeroFigure, HeroSprite, IconButton, Input, OryxIcon, Panel, Segmented, Skeleton, Stat, Stats } from '../components';
import { ItemIcon } from '../inventory/ItemCard';
import { rpc, useToasts } from '../../core/net/reducers';
import { CLASS_NAMES } from '../../core/sim/adapt';
import type { WasmAbility } from '../../core/sim/actions';
import { wasmModule } from '../../core/sim/wasm';
import { describeAbility } from '../../core/sim/describe';
import { getBridge } from '../../core/encounter/session';
import { coatFrame } from '../../core/sim/companion';
import type { CompanionDef, HeroPreview, KitItem, WasmSimBridge } from '../../core/sim/WasmSimBridge';
import {
  abilityAvailability, backgroundsFor, BUILD_OPTIONS, buildWord, CALLING_KIND, calledArgs, callingBlock, callingsFor, cap, CLASS_ROLE, classAbilities, classFeatures, costWord, defaultBackground, defaultCalling, detailsDoc,
  firstAbilities, forgedToast, resourceFill, seedOf, isWorn, rangeWord, SLOT_NAMES, slotWord, SPECIES, startingGold, toggleSpell, universalAbilities,
  type CallingChoice, type DetailsKind, type DetailsTarget,
} from './forge';
import { CallingSlot, Pick } from './forge/Calling';
import { Slot, type SlotKey } from './forge/Slot';
import { DetailsButton, DetailsSheet } from './forge/Details';
import { suggestName } from './forge/names';
import { outfitOf, type HeroLook } from '../../game/heroKit';
import { HERO_VOICES, playHeroLine, type HeroVoiceCode } from '../../core/audio/voices';
import { playSfx } from '../../core/audio';
import { usePlace } from '../../core/audio/places';
import { useHeroCallings, useHeroDyes, useHeroOrigins, useHeroVoices } from '../../core/subscriptions/hooks';
import type { Hero } from '../../bindings/types';
import './forge.css';

export interface HeroForgeProps {
  worldId: bigint;
  /** This hero may not be made now (the company is full, the purse is short or has closed,
   *  docs/58 FO2): the Forge button is disabled and `capNote` shown. */
  rosterFull?: boolean;
  capNote?: string;
  onForged?: (name: string) => void;
  /** One column regardless of width (for a modal). */
  compact?: boolean;
  /** Founding gold this hero costs (docs/58 FO2): 0 for a player's first hero. */
  fee?: number;
  /** Remake this hero instead of forging a new one (docs/82 LB1): the forge opens on their
   *  choices and saves them with `reforge_hero`; no fee. */
  hero?: Hero;
}

/** The bridge appears once boot has initialised the wasm; poll until then (skeleton meanwhile). */
function useBridge(): WasmSimBridge | undefined {
  const [b, setB] = useState<WasmSimBridge | undefined>(getBridge);
  useEffect(() => {
    if (b) return;
    const id = setInterval(() => { const x = getBridge(); if (x) { setB(x); clearInterval(id); } }, 100);
    return () => clearInterval(id);
  }, [b]);
  return b;
}

export function HeroForge(props: HeroForgeProps) {
  const bridge = useBridge();
  if (!bridge) {
    return (
      <Panel title="Forge a hero" className={`fk-forge ${props.compact ? 'fk-forge--compact' : ''}`} data-testid="forge" data-ready="false" aria-busy="true">
        <div className="fk-forge__grid">
          <div className="fk-forge__choices"><Skeleton kind="title" width="40%" /><Skeleton kind="card" /><Skeleton kind="card" /><Skeleton kind="card" /></div>
          <div className="fk-forge__preview"><Skeleton kind="circle" /><Skeleton kind="text" /><Skeleton kind="card" /></div>
        </div>
      </Panel>
    );
  }
  return <ForgeBody {...props} bridge={bridge} />;
}

function tryPreview(bridge: WasmSimBridge, cls: number, species: number, build: number, background: number, loadout: number, choice?: CallingChoice): HeroPreview | undefined {
  const c = choice ? calledArgs(cls, choice) : undefined;
  try { return bridge.previewHero(cls, species, build, background, loadout, 1, c?.pick ?? 0, c?.spells ?? []); } catch (err) { console.warn('[forge] preview_hero failed', err); return undefined; }
}

/** What the preview tags a calling-granted ability with. */
const GRANT_TAG: Record<string, string> = { feat: 'feat', specialty: 'specialty', patron: 'prayer', spells: 'spell', companion: 'companion', oath: 'oath', circle: 'circle', instrument: 'song' };

/** The tiles inside an open slot. */
function Tiles({ label, grid, children }: { label: string; grid: 'class' | 'species' | 'wide'; children: ReactNode }) {
  return <div className={`fk-forge__cards fk-forge__cards--${grid}`} role="group" aria-label={label}>{children}</div>;
}

/** The name's dice (docs/137 HN3): a five-face drawn in the button's ink. */
function NameDie() {
  return (
    <svg className="fk-forge__die" viewBox="0 0 16 16" width="18" height="18" aria-hidden="true" shapeRendering="crispEdges">
      <rect x="1.5" y="1.5" width="13" height="13" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      {[[5, 5], [11, 5], [8, 8], [5, 11], [11, 11]].map(([x, y]) => <rect key={`${x}${y}`} x={x! - 1} y={y! - 1} width="2" height="2" fill="currentColor" />)}
    </svg>
  );
}

function KitLine({ item, showSlot }: { item: KitItem; showSlot?: boolean }) {
  const slot = isWorn(item) ? SLOT_NAMES[item.slot!] : undefined;
  return (
    <li className="fk-forge__kit-item">
      <ItemIcon def={item.def} tier={item.tier} name={item.name} scale={2} framed title={item.name} />
      <span className="fk-forge__kit-name">{item.name}</span>
      {showSlot && slot && <span className="fk-forge__kit-slot">{slot}</span>}
    </li>
  );
}

function ForgeBody({ worldId, rosterFull, capNote, onForged, compact, fee = 0, hero, bridge }: HeroForgeProps & { bridge: WasmSimBridge }) {
  const push = useToasts((s) => s.push);
  const [cls, setCls] = useState(hero?.class ?? 0);
  const [species, setSpecies] = useState(hero?.species ?? 0);
  // docs/137 HN: a new hero starts on a suggested name (and, HN5, an even-odds voice, whose gender the
  // names follow); while the name is still the suggestion it follows the species, class and voice, and
  // the dice draw a fresh one. A typed (or edited hero's) name stays.
  const [voice, setVoice] = useState<HeroVoiceCode>(() => (hero || Math.random() < 0.5 ? 0 : 1));
  const [firstName] = useState(() => (hero ? '' : suggestName(0, 0, voice)));
  const suggested = useRef(firstName);
  const [name, setName] = useState(hero?.name ?? suggested.current);
  const nameRef = useRef(name);
  nameRef.current = name;
  const suggest = useCallback((sp: number, c: number, g: HeroVoiceCode, avoid: readonly string[] = []) => {
    const n = suggestName(sp, c, g, Math.random, [nameRef.current, ...avoid]);
    suggested.current = n;
    setName(n);
  }, []);
  const lastPicks = useRef(`${species}:${cls}:${voice}`);
  useEffect(() => {
    const key = `${species}:${cls}:${voice}`;
    if (key === lastPicks.current) return;
    lastPicks.current = key;
    const n = nameRef.current.trim();
    if (!n || n === suggested.current) suggest(species, cls, voice);
  }, [species, cls, voice, suggest]);
  const [build, setBuild] = useState(hero?.build ?? 0);
  const [background, setBackground] = useState(0);
  const [loadout, setLoadout] = useState(0);
  // docs/142: the clothes' dye (0 as drawn)
  const [dye, setDye] = useState(0);
  const [busy, setBusy] = useState(false);
  const callings = bridge.callings();
  const [choice, setChoice] = useState<CallingChoice>(() => defaultCalling(callings, hero?.class ?? 0));
  // editing: start from the hero's own choices once their origin, calling and voice rows are in
  const origins_ = useHeroOrigins(hero ? worldId : undefined);
  const callingRows = useHeroCallings(hero ? worldId : undefined);
  const voices = useHeroVoices(hero ? worldId : undefined);
  const dyes = useHeroDyes(hero ? worldId : undefined);
  const seeded = useRef<bigint | undefined>(undefined);
  useEffect(() => {
    if (!hero || seeded.current === hero.id) return;
    const o = origins_.find((r) => r.heroId === hero.id);
    if (!o && origins_.length === 0) return;   // the origin rows are still loading
    seeded.current = hero.id;
    const v = voices.find((r) => r.heroId === hero.id)?.voice;
    const sd = seedOf(callings, hero, o, callingRows.find((r) => r.heroId === hero.id), v);
    setName(sd.name); setCls(sd.cls); setSpecies(sd.species); setBuild(sd.build); setVoice(sd.voice as HeroVoiceCode);
    setBackground(sd.background); setLoadout(sd.loadout); setChoice(sd.choice);
    setDye(dyes.find((r) => r.heroId === hero.id)?.dye ?? 0);
  }, [hero, origins_, callingRows, voices, dyes, callings]);
  const [details, setDetails] = useState<DetailsTarget>();
  const [open, setOpen] = useState<SlotKey>();
  const toggle = useCallback((k: SlotKey) => setOpen((o) => (o === k ? undefined : k)), []);
  const close = useCallback(() => setOpen(undefined), []);
  const openDetails = useCallback((kind: DetailsKind, id: number) => setDetails({ kind, id }), []);
  const closeDetails = useCallback(() => setDetails(undefined), []);

  const origins = bridge.origins();
  const catalogue = useMemo(() => wasmModule().abilities() as WasmAbility[], []);
  const classId = CLASS_NAMES[cls] ?? 'fighter';
  const loadouts = origins.loadouts.filter((l) => l.class === cls).sort((a, b) => a.id - b.id);
  const bg = origins.backgrounds.find((b) => b.id === background) ?? origins.backgrounds[0];
  // the class's own backgrounds first (docs/100)
  const backgrounds = useMemo(() => backgroundsFor(origins, cls), [origins, cls]);
  const preview = useMemo(() => tryPreview(bridge, cls, species, build, background, loadout, choice), [bridge, cls, species, build, background, loadout, choice]);
  const look = useMemo<HeroLook>(() => ({ species, sex: voice === 1 ? 'female' : 'male', dye, outfit: outfitOf((preview?.kit ?? []).map((k) => ({ def: k.def, equippedSlot: k.slot }))) }), [species, voice, dye, preview]);
  // Class-card headlines: each class with the current species/background, its starter kit and default calling.
  const headlines = useMemo(() => CLASS_NAMES.map((_, i) => tryPreview(bridge, i, species, build, background, 0, defaultCalling(callings, i))), [bridge, callings, species, build, background]);
  const abilities = useMemo(() => classAbilities(catalogue, classId), [catalogue, classId]);
  const universal = useMemo(() => universalAbilities(catalogue, preview?.stats.abilities ?? []).map((a) => a.name), [catalogue, preview]);
  const doc = useMemo(() => (details ? detailsDoc(details, { callings, catalogue, backgrounds: origins.backgrounds, loadouts: origins.loadouts, cls }) : undefined),
    [details, callings, catalogue, origins, cls]);

  const kind = CALLING_KIND[cls] ?? 'feat';
  const calling = callingsFor(callings, cls).find((d) => d.id === choice.pick);
  const beast: CompanionDef | undefined = kind === 'companion' ? callings.companions.find((c) => c.calling === choice.pick) : undefined;
  // ids the calling granted, for the preview's tag (a wizard's chosen spells, a feat, a prayer)
  const granted = new Set(kind === 'spells' ? choice.spells : calling?.grants ?? []);
  const why = callingBlock(callings, cls, choice);

  const pickClass = (i: number) => {
    if (i !== cls) { setChoice(defaultCalling(callings, i)); setBackground(defaultBackground(origins, i)); }
    setCls(i); setLoadout(0); close();
  };
  usePlace({ key: 'place.smithy' });   // docs/44: the forge's own smithy, over whatever screen it opens on
  const trimmed = name.trim();
  const full = !hero && !!rosterFull;
  const blocked = busy || !trimmed || full || !preview || !!why;
  const forge = async () => {
    if (blocked) return;
    setBusy(true);
    const c = calledArgs(cls, choice);
    const r = hero
      ? await rpc.reforgeHero(hero.id, trimmed, cls, species, build, background, loadout, voice, c.pick, c.spells, c.petName, c.petCoat, dye)
      : await rpc.forgeHeroCalled(worldId, trimmed, cls, species, build, background, loadout, voice, c.pick, c.spells, c.petName, c.petCoat, dye);
    setBusy(false);
    if (!r.ok) return;
    playSfx('ui.forged');
    push(hero ? `${trimmed} is remade.` : forgedToast(trimmed, classId), 'success');
    if (!hero) suggest(species, cls, voice, [trimmed]);
    onForged?.(trimmed);
  };
  const title = hero ? `Edit ${hero.name}` : 'Forge a hero';

  const s = preview?.stats;
  const resource = s?.resource.kind ?? '';
  const worn = (preview?.kit ?? []).filter(isWorn).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  const bag = (preview?.kit ?? []).filter((k) => !isWorn(k));
  // only what this hero has at level 1: an unchosen feat, prayer or spell is not listed
  const first = firstAbilities(catalogue, classId, s?.abilities ?? []);
  const features = classFeatures(catalogue, classId);
  const later = abilities.filter((a) => a.level_req >= 2 && a.level_req <= 5);
  // docs/58 FO1: the World's starting gold is the player's founding purse, not the hero's
  const gold = startingGold(bg?.gold ?? 0);
  const head = headlines[cls]?.stats;
  const sp = SPECIES[species];
  const lo = loadouts.find((l) => l.id === loadout);

  return (
    <Panel title={title} className={`fk-forge ${compact ? 'fk-forge--compact' : ''}`} data-testid="forge" data-ready="true" data-editing={hero ? 'true' : undefined}
      actions={full ? <Badge tone="amber">roster full</Badge> : undefined}>
      <div className="fk-forge__grid">
        <div className="fk-forge__choices">
          <Field label="Name" htmlFor="fk-forge-name" hint="Up to 24 letters. One fit for their species, class and voice is suggested; the dice draw another." className="fk-forge__name">
            <div className="fk-forge__name-row">
              <Input id="fk-forge-name" data-testid="forge-name" placeholder="What do they call this hero?" maxLength={24} value={name} autoComplete="off"
                data-suggested={name === suggested.current ? 'true' : undefined}
                onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void forge(); }} disabled={full} />
              <IconButton label="Suggest another name" title={`Suggest another ${voice === 1 ? 'feminine' : 'masculine'} ${SPECIES[species]?.name ?? ''} ${cap(classId)} name`} data-testid="forge-name-random" data-sfx="ui.forge"
                onClick={() => suggest(species, cls, voice)} disabled={full}><NameDie /></IconButton>
            </div>
          </Field>

          <div className="fk-forge__slots">
            <Slot slot="class" label="Class" open={open === 'class'} onToggle={toggle}
              art={<ClassIcon classId={classId} scale={2} title={cap(classId)} />} value={cap(classId)}
              sub={<>{CLASS_ROLE[cls]}{head && <> · <b>{head.hp_max}</b> HP · move <b>{head.move_tiles}</b></>}</>}>
              <Tiles label="Class" grid="class">
                {CLASS_NAMES.map((c, i) => {
                  const h = headlines[i]?.stats;
                  return (
                    <Card key={c} as="button" interactive column selected={cls === i} className="fk-forge__choice fk-forge__class" aria-pressed={cls === i}
                      data-testid="forge-class" data-class-index={i} data-sfx="ui.forge" onClick={() => pickClass(i)}>
                      <span className="fk-forge__class-icon"><ClassIcon classId={c} scale={2} title={cap(c)} /></span>
                      <span className="fk-forge__choice-name">{cap(c)}</span>
                      <span className="fk-forge__choice-sub">{CLASS_ROLE[i]}</span>
                      {h && <span className="fk-forge__class-line"><b>{h.hp_max}</b> HP · move <b>{h.move_tiles}</b> · {h.resource.kind}</span>}
                      {classFeatures(catalogue, c).map((f) => <span key={f.id} className="fk-forge__class-feature" data-testid="forge-class-feature-tag">★ {f.name}</span>)}
                    </Card>
                  );
                })}
              </Tiles>
            </Slot>

            <CallingSlot cls={cls} callings={callings} catalogue={catalogue} choice={choice} onChange={setChoice} onDetails={openDetails}
              onToggleSpell={(id) => setChoice((c) => ({ ...c, spells: toggleSpell(c.spells, id, callings.wizard_picks) }))} resource={resource || 'resource'}
              open={open === 'calling'} onToggle={toggle} onPicked={close} />

            <Slot slot="species" label="Species" open={open === 'species'} onToggle={toggle} hint="A flat passive, applied once at creation."
              value={sp?.name} sub={sp?.passive} aside={sp && <DetailsButton kind="species" id={sp.index} name={sp.name} onOpen={openDetails} />}>
              <Tiles label="Species" grid="species">
                {SPECIES.map((sp) => (
                  <Pick key={sp.name} details={<DetailsButton kind="species" id={sp.index} name={sp.name} onOpen={openDetails} />}>
                    <Card as="button" interactive column selected={species === sp.index}
                      className="fk-forge__choice fk-forge__species" aria-pressed={species === sp.index} data-testid="forge-species" data-species-index={sp.index} data-sfx="ui.forge"
                      onClick={() => { setSpecies(sp.index); close(); }}>
                      <span className="fk-forge__choice-name">{sp.name}</span>
                      <span className="fk-forge__species-passive">{sp.passive}</span>
                    </Card>
                  </Pick>
                ))}
              </Tiles>
            </Slot>

            <Slot slot="background" label="Background" open={open === 'background'} onToggle={toggle} hint="Who they were before the road."
              value={bg?.name} sub={bg?.grants} aside={bg && <DetailsButton kind="background" id={bg.id} name={bg.name} onOpen={openDetails} />}>
              <Tiles label="Background" grid="wide">
                {backgrounds.map((b) => (
                  <Pick key={b.id} details={<DetailsButton kind="background" id={b.id} name={b.name} onOpen={openDetails} />}>
                    <Card as="button" interactive column selected={background === b.id} className="fk-forge__choice fk-forge__background" aria-pressed={background === b.id}
                      data-testid="forge-background" data-background-id={b.id} data-sfx="ui.tab" onClick={() => { setBackground(b.id); close(); }}>
                      <span className="fk-forge__choice-name">{b.name}</span>
                      <span className="fk-forge__blurb">{b.blurb}</span>
                      <span className="fk-forge__grants">{b.grants}</span>
                    </Card>
                  </Pick>
                ))}
              </Tiles>
            </Slot>

            <Slot slot="loadout" label="Loadout" open={open === 'loadout'} onToggle={toggle} hint={`What the ${cap(classId)} carries out of the gate.`}
              art={lo?.items[0] && <ItemIcon def={lo.items[0].def} tier={lo.items[0].tier} name={lo.items[0].name} scale={2} title={lo.items[0].name} />}
              value={lo?.name} sub={lo?.items.map((it) => it.name).join(' · ')}
              aside={lo && <DetailsButton kind="loadout" id={lo.id} name={lo.name} onOpen={openDetails} />}>
              <Tiles label="Loadout" grid="wide">
                {loadouts.map((l) => (
                  <Pick key={`${l.class}-${l.id}`} details={<DetailsButton kind="loadout" id={l.id} name={l.name} onOpen={openDetails} />}>
                    <Card as="button" interactive column selected={loadout === l.id} className="fk-forge__choice fk-forge__loadout" aria-pressed={loadout === l.id}
                      data-testid="forge-loadout" data-loadout-id={l.id} data-sfx="ui.equip_weapon" onClick={() => { setLoadout(l.id); close(); }}>
                      <span className="fk-forge__choice-name">{l.name}{l.id === 0 && <Badge tone="grey">starter</Badge>}</span>
                      <span className="fk-forge__blurb">{l.blurb}</span>
                      <span className="fk-forge__loadout-items" role="list" aria-label={`${l.name} items`}>
                        {l.items.map((it, k) => <span key={k} role="listitem"><ItemIcon def={it.def} tier={it.tier} name={it.name} scale={1} title={it.name} /><span>{it.name}</span></span>)}
                      </span>
                    </Card>
                  </Pick>
                ))}
              </Tiles>
            </Slot>
          </div>

          <section className="fk-forge__looks" aria-label="Looks and voice">
            <Field label="Build" hint="How the story tells of them; the figure is their species' own." className="fk-forge__look">
              <div data-testid="forge-build" className="fk-forge__build"><Segmented label="Build" value={build} onChange={setBuild} options={BUILD_OPTIONS} /></div>
            </Field>
            <Field label="Voice" hint="Everyone at the table hears it; a feminine voice is a woman's figure." className="fk-forge__look">
              <div data-testid="forge-voice" className="fk-forge__build">
                <Segmented label="Voice" value={voice} onChange={(v) => { setVoice(v); playHeroLine({ classId, species, voice: v }, 'ready'); }} options={HERO_VOICES} />
                <Button variant="ghost" small data-sfx="none" onClick={() => playHeroLine({ classId, species, voice }, 'ready')} data-testid="forge-voice-hear">Hear it</Button>
              </div>
            </Field>
            {/* docs/142 DY4: the colour of their clothes; a village's dyer can change it later */}
            <Field label="Dye" hint="The colour of their clothes. Metal, stone and the like keep their own; a dyer can change it later." className="fk-forge__look fk-forge__dye">
              <DyeSwatches value={dye} onChange={setDye} label="Dye" data-testid="forge-dye" />
            </Field>
          </section>
        </div>

        <aside className="fk-forge__preview" aria-label="Preview">
          <div className="fk-forge__hero">
            {/* docs/145: the hero as they will look, in the kit they start with */}
            <span className="fk-portrait fk-portrait--lg fk-portrait--glow fk-forge__portrait fk-forge__figure" data-testid="forge-figure">
              <HeroFigure look={look} scale={4} breathe title={`${cap(classId)} preview`} />
            </span>
            <div className="fk-forge__plate" data-testid="forge-plate">{trimmed || 'Unnamed hero'}</div>
            <div className="fk-forge__kind">{cap(classId)} · {SPECIES[species]?.name} · {buildWord(build)}</div>
            <div className="fk-forge__origin">{bg?.name} · {lo?.name}{calling && kind !== 'companion' ? ` · ${calling.name}${calling.title ? `, ${calling.title}` : ''}` : ''}</div>
          </div>

          {!preview || !s ? <Banner tone="warn" inline>The simulator could not preview this combination.</Banner> : (
            <>
              <Stats className="fk-forge__stats" data-testid="forge-stats" data-hp={s.hp_max} data-guard={s.guard} data-move={s.move_tiles} data-focus={s.focus} data-power={s.power} aria-label="Starting stats">
                <Stat label="HP" value={s.hp_max} />
                <Stat label="Move" value={s.move_tiles} unit="tiles" />
                <Stat label="Guard" value={s.guard} />
                <Stat label="Power" value={s.power} />
                <Stat label="Focus" value={s.focus} />
                {s.ranged_weapon ? <Stat label="Range" value={s.range} unit="tiles" /> : <Stat label="Reach" value={s.reach} />}
                <Stat label="Weapon" value={s.weapon_damage} unit="dmg" />
                <Stat label={resource} value={s.resource.max} unit={resourceFill(cls, s.resource.regen)} className="fk-forge__resource" />
              </Stats>

              <div className="fk-forge__block">
                <h4 className="fk-forge__subhead">Starting kit</h4>
                <div className="fk-forge__kit">
                  <ul className="fk-forge__kit-list" aria-label="Worn">{worn.map((k, i) => <KitLine key={`w${i}`} item={k} showSlot />)}</ul>
                  {bag.length > 0 && <ul className="fk-forge__kit-list fk-forge__kit-list--bag" aria-label="In the bag"><li className="fk-forge__kit-label">In the bag</li>{bag.map((k, i) => <KitLine key={`b${i}`} item={k} />)}</ul>}
                </div>
                <div className="fk-forge__gold" data-testid="forge-gold" data-gold={gold}><OryxIcon name="item_coin_bag" scale={1} title="gold" /><b>{gold}</b> gold in the purse</div>
                <p className="fk-forge__note" data-testid="forge-fee" data-fee={hero ? 0 : fee}>{hero ? 'Remaking a hero costs nothing: their starting kit is made anew (wherever the old one is), and trained cards, market goods and what your other heroes handed them stay, back in the pack.' : fee > 0 ? `Recruiting costs ${fee} of your founding gold.` : 'Your first hero joins free.'}</p>
              </div>

              {features.length > 0 && (
                <div className="fk-forge__block fk-forge__feature" data-testid="forge-class-feature">
                  <h4 className="fk-forge__subhead">Class ability</h4>
                  {features.map((f) => (
                    <p key={f.id} className="fk-forge__feature-line" data-ability-name={f.name}><b>★ {f.name}</b> {describeAbility(f)}</p>
                  ))}
                </div>
              )}

              <div className="fk-forge__block">
                <h4 className="fk-forge__subhead">Level 1 abilities</h4>
                <ul className="fk-forge__abilities" aria-label="Level 1 abilities">
                  {first.map((a) => {
                    const av = abilityAvailability(a, s, preview.kit);
                    return (
                      <li key={a.id} className={`fk-forge__ability ${av.available ? '' : 'fk-forge__ability--off'}`} data-testid="forge-ability" data-ability-name={a.name} data-available={String(av.available)}
                        title={av.reason}>
                        <span className="fk-forge__ability-name">{a.name}{granted.has(a.id) && <span className="fk-forge__ability-tag">{GRANT_TAG[kind]}</span>}{!a.class && <span className="fk-forge__ability-tag">{SPECIES[species]?.name}</span>}</span>
                        <Badge tone={a.slot === 'Bonus' ? 'plum' : a.slot === 'Passive' ? 'grey' : 'blue'}>{slotWord(a.slot)}</Badge>
                        <span className="fk-forge__ability-meta">{a.slot === 'Passive' ? costWord(a, resource) : `${rangeWord(a)} · ${costWord(a, resource)}`}</span>
                        {!av.available && <span className="fk-forge__ability-why">{av.reason}</span>}
                      </li>
                    );
                  })}
                </ul>
                {universal.length > 0 && <p className="fk-forge__note">Every hero also has {universal.join(', ')}.</p>}
              </div>

              {beast && calling && preview.companion && (
                <div className="fk-forge__block" data-testid="forge-companion" data-hp={preview.companion.hp_max}>
                  <h4 className="fk-forge__subhead">Companion</h4>
                  <div className="fk-forge__companion">
                    <span className="fk-forge__beast-art"><HeroSprite frame={coatFrame(beast, choice.petCoat)} scale={2} title={calling.name} /></span>
                    <div className="fk-forge__companion-text">
                      <div className="fk-forge__companion-name"><b>{choice.petName.trim() || 'Unnamed'}</b><span>the {calling.name.toLowerCase()}</span></div>
                      <div className="fk-forge__companion-line">{preview.companion.hp_max} HP · guard {preview.companion.guard} · move {preview.companion.move_tiles} · bite {preview.companion.weapon_damage}</div>
                      <div className="fk-forge__companion-line">Trick: {catalogue.find((a) => a.id === beast.trick)?.name ?? '—'}</div>
                    </div>
                  </div>
                </div>
              )}

              {later.length > 0 && (
                <div className="fk-forge__block">
                  <h4 className="fk-forge__subhead">At levels 2–5</h4>
                  <ul className="fk-forge__later" aria-label="Abilities at levels 2 to 5">
                    {later.map((a) => <li key={a.id}><span className="fk-forge__later-lvl">L{a.level_req}</span><span>{a.name}</span><span className="fk-forge__later-slot">{slotWord(a.slot)}</span></li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          <div className="fk-forge__go">
            <Button primary size="lg" block disabled={blocked} loading={busy} onClick={() => void forge()} data-testid="forge-submit"
              title={full ? capNote : !trimmed ? 'name the hero first' : why}>{hero ? `Save ${trimmed || 'hero'}` : fee > 0 ? `Recruit ${trimmed || 'hero'} · ${fee}g` : `Forge ${trimmed || 'hero'}`}</Button>
            {full && capNote && <p className="fk-forge__cap" data-testid="forge-cap-note">{capNote}</p>}
            {!full && why && <p className="fk-forge__cap" data-testid="forge-calling-note">{cap(why)}.</p>}
          </div>
        </aside>
      </div>
      <DetailsSheet doc={doc} onClose={closeDetails} />
    </Panel>
  );
}
